<template>
    <v-dialog v-model="showForm" fullscreen persistent hide-overlay transition="dialog-bottom-transition" @keydown.esc="closeFormScreen">
        <v-card tile>
            <v-toolbar dark color="primary" flat>
                <v-btn icon dark @click="closeFormScreen">
                    <v-icon>close</v-icon>
                </v-btn>
                <v-toolbar-title>Pagamento <span v-if="id" class="font-weight-light">#{{ id }}</span></v-toolbar-title>
                <v-spacer />
                <v-btn icon dark :loading="loading" @click="get" title="Atualizar">
                    <v-icon>refresh</v-icon>
                </v-btn>
            </v-toolbar>

            <v-container v-if="data" class="payment-container py-6">
                <!-- Resumo -->
                <v-card outlined class="mb-4">
                    <v-card-text class="d-flex flex-wrap align-center summary">
                        <div class="mr-8">
                            <div class="overline">Valor</div>
                            <div class="text-h3 font-weight-bold text--primary">{{ money(data.value) }}</div>
                            <v-chip small label :color="statusStyle.color" text-color="white" class="mt-2 font-weight-bold">
                                <v-icon small left>{{ statusStyle.icon }}</v-icon>{{ paymentStatusLabel(data.status) }}
                            </v-chip>
                        </div>
                        <v-divider vertical class="mr-8 hidden-xs-only" />
                        <div class="summary-details">
                            <div class="summary-item">
                                <v-icon small class="mr-2">event</v-icon>
                                <span class="grey--text mr-1">Data</span>{{ data.date || "-" }}
                            </div>
                            <div class="summary-item">
                                <v-icon small class="mr-2">ev_station</v-icon>
                                <span class="grey--text mr-1">Carregador</span>
                                <a :href="nowUrl" target="_blank" rel="noopener" class="charger-link" title="Abrir carregador em nova aba">
                                    {{ data.charger }} ({{ data.plug }}) <v-icon x-small>open_in_new</v-icon>
                                </a>
                            </div>
                            <div class="summary-item">
                                <v-icon small class="mr-2">account_balance</v-icon>
                                <span class="grey--text mr-1">Operadora</span>{{ data.operator || "-" }}<span v-if="data.server" class="grey--text"> · {{ data.server }}</span>
                            </div>
                            <div class="summary-item" v-if="data.last4">
                                <v-icon small class="mr-2">credit_card</v-icon>
                                <span class="grey--text mr-1">Cartão</span>•••• {{ data.last4 }}
                            </div>
                            <div class="summary-item" v-if="data.email">
                                <v-icon small class="mr-2">person</v-icon>
                                <span class="grey--text mr-1">Cliente</span>{{ data.email }}
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card outlined>
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                        <v-icon left>receipt_long</v-icon>Operadora
                        <v-spacer />
                        <v-btn small text color="primary" :loading="loadingInfo" @click="getPaymentInfo">
                            <v-icon small left>sync</v-icon>Consultar
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <div class="caption grey--text">Último status na operadora</div>
                        <div class="text-body-1 font-weight-medium mb-3">{{ getStatus }}</div>
                        <div v-if="data.idOperator" class="mb-3">
                            <div class="caption grey--text">Identificador na operadora</div>
                            <div class="text-body-2 text-break">{{ data.idOperator }}</div>
                        </div>
                        <template v-if="moreInfo || data.infoOnInsert">
                            <ExpandPanelSimple title="Primeiro status" v-if="data.infoOnInsert">
                                <pre class="operator-json">{{ data.infoOnInsert }}</pre>
                            </ExpandPanelSimple>
                            <ExpandPanelSimple title="Última atualização" v-if="moreInfo">
                                <pre class="operator-json">{{ moreInfo }}</pre>
                            </ExpandPanelSimple>
                        </template>
                        <div v-else class="grey--text">Nenhuma informação disponível.</div>
                    </v-card-text>
                </v-card>

                <!-- Ações irreversíveis na operadora -->
                <v-card outlined class="mt-4 operations">
                    <v-card-title class="text-subtitle-1 font-weight-bold">
                        <v-icon left color="error">warning</v-icon>Operações na operadora
                    </v-card-title>
                    <v-card-subtitle>As ações abaixo são enviadas para a operadora e não podem ser desfeitas.</v-card-subtitle>
                    <v-card-text>
                        <v-row align="center">
                            <v-col cols="12" sm="6" md="4">
                                <v-text-field v-model="valueToReturn" :rules="[rules.money]" label="Valor para capturar" v-mask="['#.##', '##.##', '###.##']" return-masked-value
                                    prefix="R$" type="number" outlined dense hide-details="auto" />
                            </v-col>
                            <v-col cols="12" sm="6" md="2">
                                <v-btn block outlined color="error" @click="returnPartialPayment">
                                    <v-icon left>price_check</v-icon>Capturar
                                </v-btn>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-btn block outlined color="error" @click="cancelPayment">
                                    <v-icon left>block</v-icon>Cancelar captura
                                </v-btn>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-btn block outlined color="error" @click="refundPayment">
                                    <v-icon left>undo</v-icon>Estornar
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-container>

            <v-container v-else class="payment-container py-12 text-center grey--text">
                <v-progress-circular v-if="loading" indeterminate color="primary" />
                <span v-else>Pagamento não encontrado.</span>
            </v-container>
        </v-card>
        <ConfirmModal ref="confirm" />
    </v-dialog>
</template>

<script>
import { mask } from "vue-the-mask";

import rules from "@/helpers/rules";
import PaymentStatus from "@/enum/PaymentStatus";
import { money, paymentStatusLabel } from "@/helpers/paymentAlerts";

import ConfirmModal from "@/components/core/ConfirmModal";
import ExpandPanelSimple from "@/components/core/ExpandPanelSimple.vue";

const STATUS_STYLES = {
    [PaymentStatus.OK]: { color: "blue", icon: "lock_clock" },
    [PaymentStatus.WAIT]: { color: "amber darken-3", icon: "hourglass_empty" },
    [PaymentStatus.FINISH]: { color: "green", icon: "check_circle" },
    [PaymentStatus.CANCELED]: { color: "grey", icon: "block" },
    [PaymentStatus.REFUND]: { color: "grey", icon: "undo" },
    [PaymentStatus.ERROR]: { color: "red", icon: "error" },
    [PaymentStatus.ERROR_ON_FINISH]: { color: "red darken-2", icon: "error" },
};

export default {
    directives: { mask },

    components: { ConfirmModal, ExpandPanelSimple },

    props: {
        id: {
            type: [Number, String],
        },
    },

    data() {
        return {
            rules,
            data: null,
            showForm: true,
            moreInfo: null,
            valueToReturn: null,
            loading: false,
            loadingInfo: false,
        }
    },

    watch: {
        id: {
            immediate: true,
            handler(newValue) {
                this.showForm = newValue != undefined;
                this.get();
            },
        },
    },

    computed: {
        getStatus() {
            if (this.moreInfo) {
                if (this.moreInfo.status && this.moreInfo.status_detail) { // mercado pago pix
                    return `${this.moreInfo.status} - ${this.moreInfo.status_detail}`;
                } else if (this.moreInfo.status) { // pagarme
                    return `${this.moreInfo.status}`;
                } else if (this.moreInfo.paymentIntent) { // stripe
                    return `${this.moreInfo.paymentIntent.status}`;
                }
            }
            return "-"
        },
        statusStyle() {
            return STATUS_STYLES[this.data.status] || { color: "grey", icon: "help" };
        },
        nowUrl() {
            return `${window.location.origin}/now/${this.data.charger}/${this.data.plug}`;
        },
    },

    methods: {
        money,
        paymentStatusLabel,

        closeFormScreen() {
            this.showForm = false;
            this.$emit("close");
        },

        openFormScreen() {
            this.showForm = true;
        },

        get() {
            if (this.id) {
                this.loading = true;
                this.$http
                    .get(`api/v2/payment/${this.id}`)
                    .then((result) => {
                        this.data = result;
                        if (result) {
                            if (result.moreInfo) {
                                this.moreInfo = result.moreInfo;
                            }
                            if (result.value) {
                                this.valueToReturn = result.value;
                            }
                            this.getPaymentInfo();
                        }
                    })
                    .catch(() => {
                        this.data = null;
                        this.moreInfo = null;
                    })
                    .finally(() => (this.loading = false));
            } else {
                this.data = null;
                this.moreInfo = null;
            }
        },

        returnPartialPayment() {
            if (Number.parseFloat(this.valueToReturn) <= Number.parseFloat(this.data.value)) {
                this.$refs.confirm
                    .open("Tem certeza?", "Será feita a tentativa de captura do valor, não será possível desfazer ação.")
                    .then((confirm) => {
                        if (confirm) {
                            this.$http
                                .postAlternativeAPI("api/v2/payment", { id: this.id, action: PaymentStatus.FINISH, value: this.valueToReturn })
                                .then((result) => {
                                    this.$eventHub.$emit("msgSuccess", result.message ? result.message : "Processado com sucesso.");
                                    this.get();
                                })
                                .catch((error) => {
                                    this.$eventHub.$emit("msgError", error.message ? error.message : "Error ao processar requisição.");
                                });
                        }
                    });
            } else {
                this.$eventHub.$emit("msgError", `Valor deve ser menos que o valor capturado inicialmente. (${this.valueToReturn} <= ${this.data.value})`);
            }
        },

        getPaymentInfo() {
            this.loadingInfo = true;
            this.$http
                .postAlternativeAPI("api/v2/payment", { id: this.id, action: "get" })
                .then((result) => {
                    if (result) {
                        this.moreInfo = result;
                    }
                })
                .catch((error) => {
                    this.moreInfo = null;
                    this.$eventHub.$emit("msgError", error.message ? error.message : "Error ao processar requisição.");
                })
                .finally(() => (this.loadingInfo = false));
        },

        cancelPayment() {
            this.$refs.confirm
                .open("Tem certeza?", "Será feita a tentativa cancelamento da reserva, não será possível desfazer ação.")
                .then((confirm) => {
                    if (confirm) {
                        this.$http
                            .postAlternativeAPI("api/v2/payment", { id: this.id, action: PaymentStatus.CANCELED })
                            .then((result) => {
                                this.$eventHub.$emit("msgSuccess", result.message ? result.message : "Processado com sucesso.");
                                this.get();
                            })
                            .catch((error) => {
                                this.$eventHub.$emit("msgError", error.message ? error.message : "Error ao processar requisição.");
                            });
                    }
                });
        },

        refundPayment() {
            this.$refs.confirm
                .open("Tem certeza?", "Será feita a tentativa de estorno, não será possível desfazer ação.")
                .then((confirm) => {
                    if (confirm) {
                        this.$http
                            .postAlternativeAPI("api/v2/payment", { id: this.id, action: PaymentStatus.REFUND })
                            .then((result) => {
                                this.$eventHub.$emit("msgSuccess", result.message ? result.message : "Processado com sucesso.");
                                this.get();
                            })
                            .catch((error) => {
                                this.$eventHub.$emit("msgError", error.message ? error.message : "Error ao processar requisição.");
                            });
                    }
                });
        }
    },
};
</script>

<style scoped>
.payment-container {
    max-width: 1100px;
}

.summary {
    row-gap: 16px;
}

.summary-details {
    flex: 1;
    min-width: 260px;
}

.summary-item {
    display: flex;
    align-items: center;
    padding: 4px 0;
    font-size: 1rem;
}

.charger-link {
    color: inherit;
    text-decoration: none;
}

.charger-link:hover {
    text-decoration: underline;
}

.operations {
    border-color: rgba(244, 67, 54, 0.5) !important;
}

.operator-json {
    max-height: 400px;
    overflow: auto;
    font-size: 0.75rem;
    white-space: pre-wrap;
    word-break: break-all;
}
</style>
